import React, { memo, useMemo } from 'react'
import BellOffIcon from 'transfix-icons/build/BellOffIcon'
import BellOnIcon from 'transfix-icons/build/BellOnIcon'
import Avatar from 'transfix-ui/components/Avatar'
import { TableCell } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Typography, makeStyles } from 'transfix-ui/core'
import { ArrayElement } from 'types'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { LaneAlert } from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import { formatRelativeDate } from './utils'

const MAX_ENTRIES = 2 as const

const useSavedLanesTableRecipientCellStyles = makeStyles(theme => ({
  avatarGroupWrapper: {
    minWidth: '66px',
  },
  recipientCell: {
    '& a': {
      display: 'inline',
    },
  },
  recipientTooltip: {
    textAlign: 'center',
    backgroundColor: 'transparent !important',
    '& div, & span, & svg': {
      backgroundColor: 'transparent !important',
    },
  },
  icon: {
    display: 'inline',
    marginLeft: theme.spacing(0.5),
  },
}))

interface ISavedLanesTableRecipientCell extends Pick<LaneAlert, 'id' | 'recipientSubscribers'> {}
interface IRecipientSubscriber extends ArrayElement<LaneAlert['recipientSubscribers']> {}

const getRecipientName = ({
  firstName,
  lastName,
}: Pick<IRecipientSubscriber, 'firstName' | 'lastName'>) => `${firstName} ${lastName}`

const BellIcon = memo(({ hasNotificationsEnabled }: { hasNotificationsEnabled: boolean }) => {
  const classes = useSavedLanesTableRecipientCellStyles()
  return (
    <div className={classes.icon}>{hasNotificationsEnabled ? <BellOnIcon /> : <BellOffIcon />}</div>
  )
})

const RecipientLabel = memo(
  ({
    createdBy,
    firstName,
    hasNotificationsEnabled,
    lastName,
    updatedAt,
  }: Pick<
    IRecipientSubscriber,
    'firstName' | 'lastName' | 'hasNotificationsEnabled' | 'createdBy' | 'updatedAt'
  >) => {
    const classes = useSavedLanesTableRecipientCellStyles()
    const { firstName: authorFirstName, lastName: authorLastName } = createdBy || {}
    const authorName = useMemo(() => `${authorFirstName} ${authorLastName}`, [
      authorFirstName,
      authorLastName,
    ])
    const timestamp = useMemo(() => formatRelativeDate(updatedAt), [updatedAt])
    const subscribedText = useMemo(() => `Subscribed By ${authorName}, ${timestamp}`, [
      authorName,
      timestamp,
    ])
    const showSubscriberText = useFeatureFlag('refactoredSavedLanesCreateAndUpdate')

    return (
      <div className={classes.recipientTooltip}>
        <strong>{getRecipientName({ firstName, lastName })}</strong>
        {showSubscriberText && <div>{subscribedText}</div>}
        <BellIcon hasNotificationsEnabled={hasNotificationsEnabled} />
      </div>
    )
  }
)

export const SavedLanesTableRecipientCell = memo(
  ({ id, recipientSubscribers }: ISavedLanesTableRecipientCell) => {
    const classes = useSavedLanesTableRecipientCellStyles()

    const shownRecipients = recipientSubscribers.slice(0, MAX_ENTRIES)
    const hiddenRecipients = recipientSubscribers.slice(MAX_ENTRIES, recipientSubscribers.length)
    const hiddenCount = hiddenRecipients.length

    return (
      <TableCell dataTest={`saved-lane-${id}-recipients`}>
        {shownRecipients.length <= 0 && <div>None</div>}
        <div className={classes.avatarGroupWrapper}>
          {shownRecipients.map(
            ({
              createdBy,
              firstName,
              hasNotificationsEnabled,
              lastName,
              parentUserId,
              updatedAt,
            }) => (
              <Avatar
                key={`saved-lane-${id}-recipient-${parentUserId}-link`}
                name={getRecipientName({ firstName, lastName })}
                tooltip={
                  <RecipientLabel
                    firstName={firstName}
                    lastName={lastName}
                    createdBy={createdBy}
                    updatedAt={updatedAt}
                    hasNotificationsEnabled={hasNotificationsEnabled}
                  />
                }
              />
            )
          )}
        </div>
        {hiddenCount > 0 && (
          <div>
            <Tooltip
              id={`saved-lane-${id}-recipient-tooltip`}
              trigger={
                <Typography variant='caption' color='textSecondary'>
                  +{hiddenCount} more
                </Typography>
              }>
              {hiddenRecipients.map(
                ({
                  createdBy,
                  firstName,
                  hasNotificationsEnabled,
                  lastName,
                  parentUserId,
                  updatedAt,
                }) => (
                  <RecipientLabel
                    key={`saved-lane-${id}-recipient-${parentUserId}-tooltip`}
                    firstName={firstName}
                    lastName={lastName}
                    createdBy={createdBy}
                    updatedAt={updatedAt}
                    hasNotificationsEnabled={hasNotificationsEnabled}
                  />
                )
              )}
            </Tooltip>
          </div>
        )}
      </TableCell>
    )
  }
)
